/**
 * Top-level React error boundary.
 *
 * Wraps each app's <App /> tree. When a render throws, the boundary:
 *
 *   1. Calls `reportError(err, { severity: 'fatal', tags: { source: 'react' } })`
 *      from @pc/core's global reporter so the error lands in the
 *      `errors` schema and the user is assigned a PCE-XXXXXXXX id.
 *   2. Shows <ErrorFallback shortCode={...} /> -- the paper-and-clay
 *      friendly screen with the id, support email, copy/reload buttons.
 *
 * Until the report resolves we display the fallback with the synthetic
 * `PCE-FAILED00` id so the user is never stuck on a white screen. Once
 * the actual short code comes back from the edge, we swap it in.
 *
 * The fallback `errorInfo.componentStack` is included in the tags so
 * the admin Stack panel shows which React component crashed.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError, isInstalled } from '@pc/core';
import { ErrorFallback } from './ErrorFallback';

export interface GlobalErrorBoundaryProps {
  children: ReactNode;
  /** Override the support email shown in the fallback. */
  supportEmail?: string;
  /** Override the "Back to home" href. */
  homeHref?: string;
  /** Render a custom fallback in place of the default <ErrorFallback />. */
  renderFallback?: (info: { shortCode: string; error: Error | null }) => ReactNode;
}

interface GlobalErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
  shortCode: string;
}

const PENDING_SHORT_CODE = 'PCE-…';

export class GlobalErrorBoundary extends Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  override state: GlobalErrorBoundaryState = {
    error: null,
    componentStack: null,
    shortCode: PENDING_SHORT_CODE,
  };

  static getDerivedStateFromError(error: Error): Partial<GlobalErrorBoundaryState> {
    return { error, shortCode: PENDING_SHORT_CODE };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    if (!isInstalled()) {
       
      console.error('[GlobalErrorBoundary] reporter not installed; error not sent:', error);
      this.setState({ shortCode: 'PCE-FAILED00' });
      return;
    }
    void reportError(error, {
      severity: 'fatal',
      tags: { source: 'react.error_boundary' },
      context: {
        component_stack:
          info.componentStack && info.componentStack.length > 4000
            ? info.componentStack.slice(0, 4000) + '\n…'
            : info.componentStack,
      },
    })
      .then((res) => {
        this.setState({ shortCode: res.short_code });
      })
      .catch(() => {
        this.setState({ shortCode: 'PCE-FAILED00' });
      });
  }

  override render(): ReactNode {
    const { error, componentStack, shortCode } = this.state;
    if (!error) return this.props.children;

    if (this.props.renderFallback) {
      return this.props.renderFallback({ shortCode, error });
    }

    const details = [
      error.message,
      error.stack ? '' : null,
      error.stack ?? null,
      componentStack ? '' : null,
      componentStack ? 'React component stack:' + componentStack : null,
    ]
      .filter((v): v is string => typeof v === 'string')
      .join('\n');

    return (
      <ErrorFallback
        shortCode={shortCode}
        supportEmail={this.props.supportEmail}
        homeHref={this.props.homeHref}
        technicalDetails={details || null}
        hint={
          shortCode === PENDING_SHORT_CODE
            ? 'Sending the error report…'
            : shortCode === 'PCE-FAILED00'
              ? 'We could not reach our servers; please include the page URL and a screenshot in your email.'
              : 'A copy was sent to our team automatically.'
        }
      />
    );
  }
}
